import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
    Button,
    CalendarIcon,
    ChevronEndIcon,
    ChevronStartIcon,
    CloseIcon,
    Flex,
} from "@fluentui/react-northstar";
import moment from "moment";
import { useSearchParams } from "react-router-dom";

const Container = styled.div`
    position: relative;
`;

const CalendarButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background-color: #ffffff;
    border: 1px solid rgb(209, 209, 209);
    border-radius: 4px;
    font-size: 14px;
    color: #201f1e;
    cursor: pointer;
    transition: all 0.1s ease-in-out;
    width: 100%;
    min-width: 206px;
    height: 34px;
    padding-inline: 12px;
    padding-block: 6px;
    justify-content: space-between;

    &:hover {
        background-color: #f3f2f1;
        border-color: #c8c6c4;
    }
`;

const CalendarContainer = styled.div`
    display: ${(props) => (props.$isOpen ? "block" : "none")};
    position: absolute;
    top: 100%;
    left: 0px;
    z-index: 50;
    background-color: #ffffff;
    border-radius: 0.25rem;
    box-shadow: 0 6.4px 14.4px rgba(0, 0, 0, 0.13),
        0 1.2px 3.6px rgba(0, 0, 0, 0.1);
    width: 280px;
    border: 1px solid #edebe9;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding: 4px 8px 4px 18px;
    border-bottom: 1px solid #e0e0e0;
`;

const MonthGrid = styled.div`
    width: 100%;
    padding: 4px;
`;

const WeekDaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    /* gap: 0.25rem; */
`;

const WeekDay = styled.div`
    text-align: center;
    font-size: 0.75rem;
    color: #605e5c;
    font-weight: 600;
    text-transform: uppercase;
    padding: 0.25rem 0;
`;

const DaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    /* gap: 0.25rem; */
`;

const Day = styled.div`
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    /* border-radius: 0.25rem; */
    cursor: ${(props) =>
        props.$isEmpty || props.$isPast ? "default" : "pointer"};
    color: ${(props) => {
        if (props.$isEmpty) return "transparent";
        if (props.$isPast) return "#a19f9d";
        if (props.$isSelected || props.$isPreview) return "white";
        return "#323130";
    }};
    background-color: ${(props) => {
        if (props.$isSelected) return "var(--color-primary)";
        if (props.$isPreview) return "#9399f5";
        return "transparent";
    }};
    font-weight: ${(props) => (props.$isToday ? "600" : "400")};
    transition: all 0.1s ease-in-out;
    border: 1px solid transparent;

    ${(props) =>
        !props.$isEmpty &&
        !props.$isPast &&
        `
    &:hover {
      background-color: ${
          props.$isSelected
              ? "var(--color-primary)"
              : props.$isPreview
              ? "#9399f5"
              : "#f3f2f1"
      };
      border-color: ${
          props.$isSelected || props.$isPreview ? "transparent" : "#c8c6c4"
      };
    }
  `}

    ${(props) =>
        props.$isToday &&
        !props.$isSelected &&
        !props.$isPreview &&
        `
    border-color: #0078d4;
  `}
`;

const IconButton = styled.button`
    padding: 0.5rem;
    border-radius: 0.25rem;
    color: #323130;
    border: 1px solid transparent;
    background: transparent;
    transition: all 0.1s ease-in-out;
    cursor: pointer;
    &:hover {
        background-color: #f3f2f1;
        border-color: #c8c6c4;
    }
`;

const MonthTitle = styled.div`
    font-size: 1rem;
    font-weight: 600;
    color: #323130;
    text-align: center;
`;

const SelectedDates = styled.div`
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #edebe9;
    font-size: 0.875rem;
    color: #605e5c;
`;

export function DateRangePickerOneMonth({
    resetDatesToNull = false,
    defaultStartDate = null,
    defaultEndDate = null,
    onDatesChange,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState({
        start: defaultStartDate ? new Date(defaultStartDate) : null,
        end: defaultEndDate ? new Date(defaultEndDate) : null,
    });
    const [hoverDate, setHoverDate] = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (resetDatesToNull) {
            setSelectedDates({
                start: null,
                end: null,
            });
        }
    }, [resetDatesToNull]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        onDatesChange(selectedDates);
    }, [selectedDates]);

    const getDaysInMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
        // Get the day of the week (0-6, where 0 is Sunday)
        const firstDay = new Date(
            date.getFullYear(),
            date.getMonth(),
            1
        ).getDay();
        // Convert to Monday-based index (0-6, where 0 is Monday)
        return (firstDay + 6) % 7;
    };

    const generateMonthDays = (date) => {
        const daysInMonth = getDaysInMonth(date);
        const firstDay = getFirstDayOfMonth(date);
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(null);
        }

        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(date.getFullYear(), date.getMonth(), i));
        }

        return days;
    };

    const handleDateClick = (date) => {
        if (
            !selectedDates.start ||
            (selectedDates.start && selectedDates.end)
        ) {
            setSelectedDates({ start: date, end: null });
        } else {
            if (date < selectedDates.start) {
                setSelectedDates({ start: date, end: selectedDates.start });
            } else {
                setSelectedDates({ start: selectedDates.start, end: date });
            }
        }
    };

    const isDateSelected = (date) => {
        if (!date || !selectedDates.start) return false;
        if (selectedDates.start && selectedDates.end) {
            return date >= selectedDates.start && date <= selectedDates.end;
        }
        return selectedDates.start.toDateString() === date.toDateString();
    };

    const isDateInPreview = (date) => {
        if (!date || !selectedDates.start || !hoverDate || selectedDates.end)
            return false;
        const start = selectedDates.start;
        const end = hoverDate;
        return (
            date > (start < end ? start : end) &&
            date < (start < end ? end : start)
        );
    };

    const isToday = (date) => {
        if (!date) return false;
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isPastDate = (date) => {
        if (!date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const nextMonth = () => {
        setCurrentDate(
            new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
        );
    };

    const prevMonth = () => {
        setCurrentDate(
            new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
        );
    };

    const resetDates = () => {
        return setSelectedDates({ start: null, end: null });
    };

    const monthNames = [
        "Jan",
        "Fév",
        "Mar",
        "Avr",
        "Mai",
        "Jui",
        "Juil",
        "Août",
        "Sep",
        "Oct",
        "Nov",
        "Déc",
    ];

    const weekDays = ["L", "M", "M", "J", "V", "S", "D"];

    const formatDateRange = () => {
        if (!selectedDates.start) return "Période";
        if (!selectedDates.end)
            return `${selectedDates.start.toLocaleDateString("fr-FR")}`;
        return `${selectedDates.start.toLocaleDateString(
            "fr-FR"
        )} - ${selectedDates.end.toLocaleDateString("fr-FR")}`;
    };

    return (
        <Container ref={containerRef}>
            <CalendarButton onClick={() => setIsOpen(!isOpen)}>
                {formatDateRange()}
                {selectedDates.start || selectedDates.end ? (
                    <CloseIcon onClick={resetDates} />
                ) : (
                    <CalendarIcon outline />
                )}
            </CalendarButton>

            <CalendarContainer $isOpen={isOpen}>
                <Header>
                    <MonthTitle>
                        {monthNames[currentDate.getMonth()]}{" "}
                        {currentDate.getFullYear()}
                    </MonthTitle>
                    <Flex>
                        <IconButton onClick={prevMonth}>
                            <ChevronStartIcon />
                        </IconButton>
                        <IconButton onClick={nextMonth}>
                            <ChevronEndIcon />
                        </IconButton>
                    </Flex>
                </Header>

                <MonthGrid>
                    <WeekDaysGrid>
                        {weekDays.map((day, index) => (
                            <WeekDay key={day + "-" + index}>{day}</WeekDay>
                        ))}
                    </WeekDaysGrid>
                    <DaysGrid>
                        {generateMonthDays(currentDate).map((date, idx) => (
                            <Day
                                key={idx}
                                $isEmpty={!date}
                                $isSelected={isDateSelected(date)}
                                $isPreview={isDateInPreview(date)}
                                $isToday={isToday(date)}
                                $isPast={false}
                                onClick={() =>
                                    date &&
                                    // !isPastDate(date) &&
                                    handleDateClick(date)
                                }
                                onMouseEnter={() =>
                                    date &&
                                    // !isPastDate(date) &&
                                    setHoverDate(date)
                                }
                                onMouseLeave={() => setHoverDate(null)}
                            >
                                {date?.getDate()}
                            </Day>
                        ))}
                    </DaysGrid>
                </MonthGrid>
                {/* {selectedDates.start.toLocaleDateString("fr-FR")}
                    {selectedDates.end.toLocaleDateString("fr-FR")} */}
            </CalendarContainer>
        </Container>
    );
}
